import { z } from 'zod';
import {
    CardPointeRefundOutcome,
    type CardPointeRefundRequestBody,
    type CardPointeRefundRequestDetails,
    type CardPointeRefundResponseBody,
    type CardPointeRefundResponseDetails,
} from '../../../types/ipg/cardpointe/cardpointe-refund.types';

export const CardPointeRefundAmountSchema: z.ZodString = z.string().regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeRefundRequestBodySchema: z.ZodObject<{
    merchid: z.ZodString;
    retref: z.ZodString;
    amount: z.ZodOptional<z.ZodString>;
    orderid: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchid: z.string().min(1),
    retref: z.string().min(1),
    amount: CardPointeRefundAmountSchema.optional(),
    orderid: z.string().min(1).optional(),
}) satisfies z.ZodType<CardPointeRefundRequestBody>;

export const CardPointeRefundRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/cardconnect/rest/refund'>;
    body: typeof CardPointeRefundRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/cardconnect/rest/refund'),
    body: CardPointeRefundRequestBodySchema,
}) satisfies z.ZodType<CardPointeRefundRequestDetails>;

export const CardPointeRefundResponseBodySchema: z.ZodObject<{
    respstat: z.ZodUnion<readonly [z.ZodLiteral<'A'>, z.ZodLiteral<'B'>, z.ZodLiteral<'C'>]>;
    merchid: z.ZodOptional<z.ZodString>;
    retref: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodString>;
    currency: z.ZodOptional<z.ZodString>;
    orderid: z.ZodOptional<z.ZodString>;
    respcode: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    resptext: z.ZodOptional<z.ZodString>;
    receipt: z.ZodOptional<z.ZodUnknown>;
    receiptObj: z.ZodOptional<z.ZodUnknown>;
    declineCategory: z.ZodOptional<z.ZodString>;
    declineCategoryText: z.ZodOptional<z.ZodString>;
    fee_amount: z.ZodOptional<z.ZodString>;
    fee_value: z.ZodOptional<z.ZodString>;
    fee_type: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'SURCHRG'>, z.ZodLiteral<'SURCHRG_WAIVED'>]>
    >;
    fee_format: z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<'percent'>, z.ZodLiteral<''>]>>;
    fee_authcode: z.ZodOptional<z.ZodString>;
    fee_merchid: z.ZodOptional<z.ZodString>;
    fee_retref: z.ZodOptional<z.ZodString>;
}> = z.object({
    respstat: z.union([z.literal('A'), z.literal('B'), z.literal('C')]),
    merchid: z.string().min(1).optional(),
    retref: z.string().min(1).optional(),
    amount: z.string().min(1).optional(),
    currency: z.string().min(1).optional(),
    orderid: z.string().min(1).optional(),
    respcode: z.string().min(1).optional(),
    respproc: z.string().min(1).optional(),
    resptext: z.string().min(1).optional(),
    receipt: z.unknown().optional(),
    receiptObj: z.unknown().optional(),
    declineCategory: z.string().min(1).optional(),
    declineCategoryText: z.string().min(1).optional(),
    fee_amount: z
        .string()
        .regex(/^\d+(?:\.\d{2})?$/)
        .optional(),
    fee_value: z.string().regex(/^\d+$/).optional(),
    fee_type: z.union([z.literal('SURCHRG'), z.literal('SURCHRG_WAIVED')]).optional(),
    fee_format: z.union([z.literal('percent'), z.literal('')]).optional(),
    fee_authcode: z.string().optional(),
    fee_merchid: z.string().optional(),
    fee_retref: z.string().optional(),
}) satisfies z.ZodType<CardPointeRefundResponseBody>;

export const CardPointeRefundResponseDetailsSchema: z.ZodType<CardPointeRefundResponseDetails> =
    z.discriminatedUnion('outcome', [
        z.object({
            outcome: z.literal(CardPointeRefundOutcome.APPROVED),
            httpStatus: z.literal(200),
            body: CardPointeRefundResponseBodySchema.extend({
                respstat: z.literal('A'),
                retref: z.string().min(1),
                merchid: z.string().min(1),
                amount: z.string().min(1),
            }),
        }),
        z.object({
            outcome: z.literal(CardPointeRefundOutcome.DECLINED),
            httpStatus: z.literal(200),
            body: CardPointeRefundResponseBodySchema.extend({
                respstat: z.union([z.literal('B'), z.literal('C')]),
                resptext: z.string().min(1),
            }),
        }),
        z.object({
            outcome: z.literal(CardPointeRefundOutcome.MALFORMED_RESPONSE),
            httpStatus: z.number().int().positive(),
            body: z.unknown(),
            message: z.string().min(1),
        }),
    ]);
